import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useGetAttachmentUploadUrl from './useAttachmentUploadUrlMutation';
import useAttachmentCreate from './useAttachmentCreateMutation';
import useAttachmentDeleteMutation from './useAttachmentDeleteMutation';
import useGetAttachmentsList from './useAttachmentListQuery';
import getAttachmentQuery from './useAttachmentQuery';
import useAttachmentTenantStorage from './useAttachmentTenantStorageQuery';
import useToast from '#/common/hooks/useToast';
import { buildFilters } from '#/common/utils/buildFilters';
import { SortDirection } from '#/graphql/types';

export type UseAttachmentManagerOptions = {
  relatedTable: string; // e.g. 'models'
  relatedItem: string; // record id
  attachmentType?: 'general' | 'profile';
  relatedField?: string; // e.g. picture
};

// Minimal shape we rely on from the attachments list
export interface AttachmentListItem {
  id: string;
  mimeType?: string | null;
  path?: string | null;
  originalFileName?: string | null;
  downloadUrl?: string | null;
  size?: number | null;
  type?: string | null;
}

const buildConditionString = (relatedTable: string, relatedItem: string, type: string) => {
  const parts: string[][] = [
    ['relatedItem', '=', relatedItem],
    ['relatedTable', '=', relatedTable],
  ];
  if (type) parts.push(['type', '=', type]);
  return JSON.stringify(buildFilters(parts, 'and'));
};

/** Maximum size allowed for a single uploaded file (25 MB). See TMM-101. */
export const MAX_SIZE_BYTES = 25 * 1024 * 1024;

/** Maximum total storage per tenant (5 GB). See TMM-101. */
export const MAX_TOTAL_STORAGE_BYTES = 5 * 1024 * 1024 * 1024;

const useAttachmentManager = ({
  relatedTable,
  relatedItem,
  attachmentType = 'general',
  relatedField,
  tenantId: tenantIdProp,
}: UseAttachmentManagerOptions & { tenantId?: string | null }) => {
  // local state
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previewSrc, setPreviewSrc] = useState<Record<string, string>>({});
  const [busyDeleteId, setBusyDeleteId] = useState<string | null>(null);

  // Try to get tenantId from URL if not provided
  const [searchParams] = useSearchParams();
  const tenantId = tenantIdProp || searchParams.get('tenantId');

  // existing hooks
  const { getAttachmentUploadUrls } = useGetAttachmentUploadUrl();
  const { createAttachment } = useAttachmentCreate();
  const { deleteAttachment } = useAttachmentDeleteMutation();
  const { getAttachment } = getAttachmentQuery();
  const { totalUsedBytes } = useAttachmentTenantStorage(tenantId);
  const { alert } = useToast();

  const listQuery = useGetAttachmentsList({
    filter: buildConditionString(relatedTable, relatedItem, attachmentType),
    sortBy: { field: 'createdAt', direction: SortDirection.ASC },
  });

  const currentAttachments: AttachmentListItem[] = useMemo(() => {
    const raw = listQuery?.data?.getAttachmentList?.data;
    return Array.isArray(raw) ? (raw as AttachmentListItem[]) : [];
  }, [listQuery?.data]);

  // helpers
  const refetch = useCallback(async () => {
    if (typeof listQuery?.refetch === 'function') {
      await listQuery.refetch();
    }
  }, [listQuery]);

  const pickCurrentImageAttachment = useCallback((): AttachmentListItem | null => {
    const imgs = (currentAttachments || []).filter(
      (a) => typeof a?.mimeType === 'string' && a.mimeType!.includes('image'),
    );

    return imgs.length ? imgs[0] : null;
  }, [currentAttachments]);

  const clearPreview = useCallback(() => {
    Object.values(previewSrc).forEach((url) => URL.revokeObjectURL(url));

    setPreviewSrc({});
    setSelectedFiles([]);
  }, [previewSrc]);

  const onPickFile = useCallback(
    (f: File | File[]) => {
      if (!f) return;
      const files = Array.isArray(f) ? f : [f];

      files.forEach((file) => {
        if (file.size > MAX_SIZE_BYTES) {
          throw new Error(
            `File is too large. Max size is ${Math.round(MAX_SIZE_BYTES / (1024 * 1024))} MB.`,
          );
        }
      });

      const fileUrls = files.reduce(
        (urls, file) => {
          const url = URL.createObjectURL(file);
          return {
            ...urls,
            [file.name]: url,
          };
        },
        {} as Record<string, string>,
      );

      setPreviewSrc(fileUrls);
      setSelectedFiles(files);
    },
    [MAX_SIZE_BYTES],
  );

  // Open attachment in a new tab (uses presigned download URL)
  const open = useCallback(
    async (attachmentId: string) => {
      const resp: any = await getAttachment(attachmentId);
      const downloadUrl = resp?.data?.getAttachment?.data?.downloadUrl as string | undefined;
      if (downloadUrl) window.open(downloadUrl, '_blank');
    },
    [getAttachment],
  );

  // Upload selected files
  const upload = useCallback(
    async (filesToUpload?: File[]) => {
      filesToUpload = filesToUpload ?? selectedFiles ?? undefined;
      if (!filesToUpload) return;

      const urlResponse = await getAttachmentUploadUrls(
        filesToUpload.map((fileToUpload) => ({
          fileName: fileToUpload.name,
          size: fileToUpload.size,
          contentType: fileToUpload.type,
          relatedTable,
          relatedItem,
          type: attachmentType,
        })),
      );

      const uploadInfos = Array.isArray(urlResponse)
        ? urlResponse.filter((u) => u?.uploadUrl && u.uploadName)
        : undefined;

      if (!uploadInfos) {
        throw new Error('Failed to get upload URL');
      }

      await Promise.all(
        uploadInfos
          .map((info) => ({
            ...info,
            file: filesToUpload?.find((f) => f.name === info?.originalFileName),
          }))
          .filter((info) => info.file && info.uploadUrl && info.uploadName && info.originalFileName)
          .map(async (info) => {
            await fetch(info.uploadUrl as string, {
              method: 'PUT',
              headers: { 'Content-Type': info.file!.type },
              body: info.file,
            });

            await createAttachment({
              fileName: String(info.uploadName).split('/').pop() || '',
              size: info.file!.size,
              mimeType: info.file!.type,
              relatedTable,
              relatedItem,
              relatedField,
              type: attachmentType,
              path: info.uploadName!,
              originalFileName: info.originalFileName!,
            });
          }),
      );
      await refetch();
      clearPreview();
      const msg =
        uploadInfos.length === 1 && uploadInfos[0]
          ? `File ${uploadInfos[0].originalFileName} uploaded successfully`
          : `${uploadInfos.length} files uploaded successfully`;
      alert('success', msg);
    },
    [
      selectedFiles,
      getAttachmentUploadUrls,
      createAttachment,
      relatedTable,
      relatedItem,
      clearPreview,
      refetch,
      tenantId,
      alert,
      attachmentType,
      relatedField,
      alert,
    ],
  );

  const reupload = useCallback(async () => {
    if (!selectedFiles) return;

    // Delete all existing attachments of type "profile"
    const deletions = currentAttachments
      .filter((x) => x.type === 'profile')
      .map(async (attachment) => {
        try {
          await deleteAttachment(attachment.id, relatedField);
        } catch (err) {
          console.warn('Failed to delete attachment during reupload', attachment.id, err);
        }
      });
    await Promise.all(deletions);
    await upload();
  }, [selectedFiles, currentAttachments, deleteAttachment, upload]);

  // Delete with confirm + refresh
  const removeCurrentImage = useCallback(async () => {
    const img = pickCurrentImageAttachment();

    if (!img) return;
    setBusyDeleteId(img.id);
    try {
      await deleteAttachment(img.id, img.type === 'profile' ? relatedField : undefined);
      clearPreview();
      refetch();
    } finally {
      setBusyDeleteId(null);
    }
  }, [pickCurrentImageAttachment, deleteAttachment, clearPreview, refetch]);

  const files: File[] = selectedFiles;
  const onSelectedFileChanged = useCallback(
    (vc: { value?: File[] } | any) => {
      const arr = vc?.value as File[] | undefined;
      if (arr) {
        onPickFile(arr);
      } else {
        clearPreview();
      }
    },
    [selectedFiles, onPickFile, clearPreview],
  );

  const remove = useCallback(
    async (arg?: string | { id: string; originalFileName?: string }) => {
      const id = typeof arg === 'string' ? arg : arg?.id;
      const name = typeof arg === 'string' ? id : arg?.originalFileName;
      if (!id) {
        await removeCurrentImage();
        return;
      }
      setBusyDeleteId(id);
      try {
        await deleteAttachment(id);
        clearPreview();
        await refetch();
        alert('success', `File ${name} deleted successfully.`);
      } catch (e) {
        console.error('Failed to delete attachment', id, e);
        alert('error', `Failed to delete file ${name}.`);
      } finally {
        setBusyDeleteId(null);
      }
    },
    [removeCurrentImage, deleteAttachment, clearPreview, refetch, alert],
  );

  const currentlyDeleting: string[] = busyDeleteId ? [busyDeleteId] : [];

  const currentlyUploading: {
    originalFileName: string;
    id: string;
    mimeType: string;
    size: number;
  }[] = selectedFiles.map((file) => ({
    originalFileName: file.name,
    id: file.name,
    mimeType: 'uploading',
    size: file.size,
  }));

  // totalUsedBytes is fetched from the backend and covers all attachments for the tenant
  const storageUsagePercent = Math.round((totalUsedBytes / MAX_TOTAL_STORAGE_BYTES) * 100);
  const isStorageWarning = storageUsagePercent >= 80 && storageUsagePercent < 99;
  const isStorageFull = storageUsagePercent >= 99;

  return {
    selectedFiles,
    previewSrc,
    busyDeleteId,
    currentAttachments,
    onPickFile,
    clearPreview,
    upload,
    reupload,
    removeCurrentImage,
    open,
    refetch,
    files,
    onSelectedFileChanged,
    remove,
    currentlyDeleting,
    currentlyUploading,
    totalUsedBytes,
    storageUsagePercent,
    isStorageWarning,
    isStorageFull,
    loadingAttachments: listQuery.loading,
  };
};

export default useAttachmentManager;
